import React, { useState } from 'react';
import styled from 'styled-components';

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

const AccordionContainer = styled.div`
  border: none;
  border-radius: 0;
  overflow: visible;
  margin-bottom: 16px;
`;

const AccordionHeader = styled.div<{ isOpen: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  background: transparent;
  border: none;
  transition: background-color 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 10px 0;
  }
`;

const AccordionTitle = styled.span`
  font-family: 'SF Pro', 'SF Pro Text', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const AccordionIcon = styled.button<{ isOpen: boolean }>`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  
  &:hover {
    color: #374151;
    background: #f3f4f6;
    border-radius: 4px;
  }
  
  svg {
    width: 16px;
    height: 16px;
    transition: all 0.2s ease;
    
    path {
      fill: currentColor;
    }
  }
  
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const AccordionContent = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  background: transparent;
`;

const AccordionBody = styled.div`
  padding: 8px 0 16px 0;
  
  @media (max-width: 768px) {
    padding: 6px 0 14px 0;
  }
`;

const Accordion: React.FC<AccordionProps> = ({ 
  title, 
  children, 
  defaultOpen = false,
  className 
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggleAccordion = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <AccordionContainer className={className} onClick={(e) => e.stopPropagation()}>
      <AccordionHeader isOpen={isOpen}>
        <AccordionTitle>{title}</AccordionTitle>
        <AccordionIcon onClick={toggleAccordion} isOpen={isOpen}>
          {isOpen ? (
            // Caret Down SVG
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M4.46938 9.39966C4.76227 9.10677 5.23715 9.10677 5.53004 9.39966L11.894 15.7636C11.9916 15.8613 12.1499 15.8613 12.2476 15.7636L18.6115 9.39966C18.9044 9.10677 19.3793 9.10677 19.6722 9.39966C19.9651 9.69256 19.9651 10.1674 19.6722 10.4603L13.3082 16.8243C12.6248 17.5077 11.5168 17.5077 10.8333 16.8243L4.46938 10.4603C4.17649 10.1674 4.17649 9.69256 4.46938 9.39966Z" 
              />
            </svg>
          ) : (
            // Caret Right SVG
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M9.39862 4.32752C9.69152 4.03463 10.1664 4.03463 10.4593 4.32752L16.8232 10.6915C17.5067 11.3749 17.5067 12.4829 16.8232 13.1664L10.4593 19.5303C10.1664 19.8232 9.69152 19.8232 9.39863 19.5303C9.10573 19.2374 9.10573 18.7625 9.39863 18.4697L15.7626 12.1057C15.8602 12.0081 15.8602 11.8498 15.7626 11.7521L9.39863 5.38818C9.10573 5.09529 9.10573 4.62041 9.39862 4.32752Z" 
              />
            </svg>
          )}
        </AccordionIcon>
      </AccordionHeader>
      <AccordionContent isOpen={isOpen}>
        <AccordionBody onClick={(e) => e.stopPropagation()}>
          {children}
        </AccordionBody>
      </AccordionContent>
    </AccordionContainer>
  );
};

export default Accordion;
